import { flatten } from 'lodash';
import type {
  FileSystemDirectoryEntry,
  FileSystemEntry,
} from '@frameio/shared/transfers/types';
import { generateArrayFromPath } from './generateArrayFromPath';
import { isFileHidden } from './isFileHidden';
import type { UploadFolderItem } from './uploadItem';
import { createFolderItem } from './uploadItem';

async function getFolderEntries(rootEntry: FileSystemDirectoryEntry) {
  // In Chrome, `folderReader.readEntries` doesn't immediately return all items
  // in the folder. It only reads items in the folder in increments of 100, so
  // `readEntries` needs to be called repeatedly until it has no entries left to
  // return.
  //
  // However, the iteration cannot be held up with `await` because the backend
  // behind `readEntries` does not handle backpressure. This means that if a
  // folder has 10k files, we have to iterate all 10k files immediately with no
  // means of breaking up this iteration.
  //
  // See https://bugs.chromium.org/p/chromium/issues/detail?id=1080990
  //
  // On a Macbook Pro 16" with SSD, iterating 10k files takes about 200ms.
  const folderReader = rootEntry.createReader();
  let allEntries: FileSystemEntry[] = [];
  let entries: FileSystemEntry[] = [];

  do {
    // eslint-disable-next-line no-await-in-loop
    entries = await new Promise((resolve, reject) => {
      folderReader.readEntries(resolve, reject);
    });

    if (entries.length) {
      allEntries = [...allEntries, ...entries];
    }
  } while (entries.length !== 0);

  return allEntries;
}

export type ProcessedFileItem = {
  type: 'FILE';
  tempParentId: string | null;
  index: number;
  entry: FileSystemEntry;
};

/**
 * Recursively get the content of a dir. Webworkers don't support the FS apis,
 * so this can only run in the main thread.
 *
 * @param {FileSystemEntry} rootEntry - The `FileSystemFileEntry` or
 *   `FileSystemDirectoryEntry` instance, usually from
 *   `item.webkitGetAsEntry()`, where `item` is an instance of
 *   `DataTransferItem`.
 * @param {number} index - Custom sort index to start from.
 * @param {Array} [items=[]] - The array that gets passed into the recursion.
 * @param {number} [tempParentId=null] - The id that identifies the folders,
 *   implemented as an auto-incrementing index that gets passed into the
 *   recursion. This id will be referred to as the `tempParentId` by its
 *   children.
 * @returns {Array} - A flat list of items, each can be either a file or folder
 *   with its associated parent index.
 */
export async function getUploadItemsFromEntry(
  rootEntry: FileSystemEntry,
  index = 0,
  items: Array<ProcessedFileItem | UploadFolderItem> = [],
  tempParentId: string | null = null
): Promise<Array<ProcessedFileItem | UploadFolderItem>> {
  if (rootEntry.isDirectory) {
    const currentFolder = createFolderItem(
      tempParentId,
      rootEntry.name,
      index,
      generateArrayFromPath(rootEntry.fullPath)
    );

    const fsEntries = await getFolderEntries(rootEntry);

    /**
     * Sort the entries so that files are listed first. This ensures
     * files in any folder are all created before folders/files they may contain.
     */
    fsEntries.sort((a, b) => {
      if (a.isFile && !b.isFile) {
        return -1;
      }
      if (!a.isFile && b.isFile) {
        return 1;
      }
      return 0;
    });

    // Recurse into each file/folder in the dir
    const treeForEntries = await Promise.all(
      fsEntries.map((entry, entryIndex) =>
        getUploadItemsFromEntry(entry, entryIndex, items, currentFolder.tempId)
      )
    );

    return [...items, currentFolder, ...flatten(treeForEntries)];
  }

  // Skip hidden files
  if (isFileHidden(rootEntry.name)) {
    return items;
  }

  return [...items, { tempParentId, index, entry: rootEntry, type: 'FILE' }];
}
